import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon, Message } from "semantic-ui-react";

import { useIsMobile } from "../../../hooks";
import { Chip } from "../../../components/common/Chip";

import { SearchIndexEntry } from "../types";
import { checkInSale, checkInIndividual } from "../actions";
import CheckInModal from "./CheckInModal";

import "./SearchResult.css";

interface SearchResultProps {
  result: SearchIndexEntry;
  pendingCheckIns: { [key: string]: boolean };
  errors: { [key: string]: string };
}

export const SearchResult: React.FC<SearchResultProps> = ({
  result,
  pendingCheckIns,
}) => {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const { sale: resultSale } = result;

  // Get the latest sale data from Redux store instead of using potentially stale search result data
  const sales = useSelector((state: any) => state.checkIn?.sales || {});
  const sale = resultSale?.id ? sales[resultSale.id.toString()] || resultSale : resultSale;

  if (!sale) return null;

  const isAllCheckedIn = sale.details.every((detail: any) => detail.checkedIn);
  const totalCount = sale.details.length;

  const handleCheckInAll = () => {
    if (!isAllCheckedIn && !pendingCheckIns[sale.id.toString()]) {
      dispatch(checkInSale(sale.id.toString()) as any);
    }
  };

  const handleCheckIn = (entry: any) => {
    const entryIndex = sale.details.findIndex((detail: any) => detail.id === entry.id);
    if (entryIndex !== -1) {
      const entry = sale.details[entryIndex];
      const entryKey = `${sale.id}-${entryIndex}`;
      const saleDetailId = entry.id?.toString() || entryIndex.toString();

      if (!entry.checkedIn && !pendingCheckIns[entryKey]) {
        dispatch(checkInIndividual(sale.id.toString(), saleDetailId) as any);
      }
    }
  };

  const getDisplayName = (customer: any) => {
    if (customer.firstName && customer.lastName) {
      return `${customer.firstName} ${customer.lastName}`;
    }
    if (customer.businessName) {
      return customer.businessName;
    }
    if (customer.firstName) {
      return customer.firstName;
    }
    if (customer.lastName) {
      return customer.lastName;
    }
    return customer.email || "Unknown";
  };

  const highlightMatchedText = (text: string) => {
    if (result.searchTerm) {
      const regex = new RegExp(`(${result.searchTerm})`, "gi");
      const parts = text.split(regex);

      return parts.map((part, index) =>
        regex.test(part) ? (
          <mark key={index} className="search-result-match">{part}</mark>
        ) : part
      );
    }

    return text;
  };

  return (
    <>
      {result.matchedEntries ? result.matchedEntries?.map(({ entry }) => (
        <div className="search-result" onClick={() => setModalOpen(true)} style={{ cursor: "pointer" }}>
          <h2>
            {highlightMatchedText(getDisplayName(entry.customer))}
          </h2>
          <span>
            Sale ID: #{highlightMatchedText(sale.id.toString())} • {entry.ticketName}{!isMobile && entry.customer.email && (<> • {highlightMatchedText(entry.customer.email)}</>)}
          </span>
          {isMobile && (
            <span>
              {highlightMatchedText(sale.customer.email)}
            </span>
          )}
          {entry.checkedIn ? (
            <Chip positive icon="check">CHECKED IN</Chip>
          ) : (
            <Icon
              name="angle right"
              size="large"
            />
          )}
        </div>
      )) : (
        <div className="search-result" onClick={() => setModalOpen(true)} style={{ cursor: "pointer" }}>
          <h2>
            {highlightMatchedText(getDisplayName(sale.customer))}
          </h2>
          <span>
            Sale ID: #{highlightMatchedText(sale.id.toString())} • {totalCount} Ticket(s){!isMobile && (<> • {highlightMatchedText(sale.customer.email)}</>)}
          </span>
          {isMobile && (
            <span>
              {highlightMatchedText(sale.customer.email)}
            </span>
          )}
          {sale.checkedIn ? (
            <Chip positive icon="check">CHECKED IN</Chip>
          ) : (
            <Icon
              name="angle right"
              size="large"
            />
          )}
        </div>
      )}

      <CheckInModal
        open={modalOpen}
        sale={sale}
        pendingCheckIns={pendingCheckIns}
        onClose={() => setModalOpen(false)}
        onCheckIn={handleCheckIn}
        onCheckInAll={handleCheckInAll}
      />
    </>
  );
};
